import React, { useEffect, useState } from 'react'
import Skeleton from 'react-loading-skeleton'
import 'react-loading-skeleton/dist/skeleton.css'

import { httpOk, GET } from 'helpers/api'
import { formatDate, formatMoney } from 'helpers/helpers'

import AnalyticsMonthly from './AnalyticsMonthly'

const cardStyle = {
	border: '1px solid var(--dark-border, #edf0f4)',
	borderRadius: '12px',
	boxShadow: '0 4px 14px rgba(15, 23, 42, 0.04)',
}

const mutedStyle = {
	color: 'var(--dark-text-muted, #8791a1)',
	fontSize: '12px',
}

const scrollListStyle = {
	height: '380px',
	overflowY: 'auto',
	paddingRight: '4px',
}

const scrollTableStyle = {
	maxHeight: '480px',
	overflowY: 'auto',
}

function formatGrowth(value) {
	const number = Number(value || 0)
	return `${number >= 0 ? '+' : ''}${number.toFixed(1).replace('.0', '')}%`
}

function formatDateLabel(dateString) {
	if (!dateString) return '—'
	return formatDate(dateString, 'dd.MM.yyyy HH:mm')
}

function staleLabel(hours) {
	if (hours == null) return 'Нет чеков'
	if (hours < 48) return `${hours} ч.`
	const days = Math.floor(hours / 24)
	return `${days} дн.`
}

function SectionCard({ title, subtitle, children, actions = null }) {
	return (
		<div className="card mb-3" style={cardStyle}>
			<div className="card-body">
				<div className={`d-flex justify-content-between align-items-start gap-3 ${title || subtitle || actions ? 'mb-3' : ''}`}>
					<div>
						{title && <h5 className="mb-1">{title}</h5>}
						{subtitle && <div style={mutedStyle}>{subtitle}</div>}
					</div>
					{actions}
				</div>
				{children}
			</div>
		</div>
	)
}

function SimpleTable({ columns, rows, emptyText = 'Нет данных', className = '' }) {
	return (
		<div className={`table-responsive ${className}`}>
			<table
				className="table align-middle mb-0"
				style={{
					fontSize: '15px',
					borderCollapse: 'separate',
					borderSpacing: 0,
				}}
			>
				<thead>
					<tr style={{ border: 'none' }}>
						{columns.map(column => (
							<th
								key={column.key}
								className={`py-3 text-muted fw-semibold ${column.className || ''}`}
								style={{
									border: 'none',
									boxShadow: 'none',
									background: 'var(--dark-surface, #fff)',
									fontSize: '13px',
									position: 'sticky',
									top: 0,
									zIndex: 1,
								}}
							>
								{column.label}
							</th>
						))}
					</tr>
				</thead>
				<tbody>
					{rows?.length > 0 ? rows.map((row, index) => (
						<tr key={row.pos_id || row.id || index}>
							{columns.map(column => (
								<td
									key={column.key}
									className={`py-3 ${column.className || ''}`}
									style={{
										borderBottom: index === rows.length - 1 ? 'none' : '1px solid var(--dark-border, #eef2f6)',
									}}
								>
									{column.render ? column.render(row, index) : row[column.key]}
								</td>
							))}
						</tr>
					)) : (
						<tr>
							<td className="text-center text-muted py-4" colSpan={columns.length}>{emptyText}</td>
						</tr>
					)}
				</tbody>
			</table>
		</div>
	)
}

function SummaryKpi({ title, value, sublabel, growth, compareLabel, icon, accent = '#4c8df0' }) {
	const number = Number(growth ?? NaN)
	const showGrowth = !Number.isNaN(number)

	return (
		<div className="card h-100" style={cardStyle}>
			<div className="card-body">
				<div className="d-flex justify-content-between align-items-start gap-2 mb-2">
					<div style={{ ...mutedStyle, color: accent }}>{title}</div>
					{icon && <i className={`uil ${icon}`} style={{ color: accent, fontSize: '20px' }}></i>}
				</div>
				<div style={{ fontSize: '28px', fontWeight: 800, color: 'var(--dark-text-strong, #111827)', letterSpacing: '-0.5px' }}>
					{value}
				</div>
				{sublabel && <div style={{ ...mutedStyle, marginTop: '4px' }}>{sublabel}</div>}
				{showGrowth && (
					<div className="d-flex align-items-center gap-2 mt-2" style={{ fontSize: '12px' }}>
						<span
							style={{
								color: number >= 0 ? 'var(--dark-fg-success, #1f9d57)' : '#d23434',
								background: number >= 0 ? 'var(--dark-tint-success, #e7f8ef)' : 'var(--dark-tint-danger, #fdecec)',
								borderRadius: '999px',
								padding: '2px 7px',
								fontWeight: 600,
							}}
						>
							{number >= 0 ? '↗' : '↘'} {formatGrowth(growth)}
						</span>
						{compareLabel && <span className="text-muted">{compareLabel}</span>}
					</div>
				)}
			</div>
		</div>
	)
}

function AgentRanking({ agents }) {
	const items = agents || []

	return (
		<div className="card h-100" style={cardStyle}>
			<div className="card-body d-flex flex-column">
				<div className="mb-3">
					<div style={{ fontWeight: 700, color: 'var(--dark-text-strong, #111827)' }}>Агенты</div>
					<div style={mutedStyle}>Подключения в этом и прошлом месяце</div>
				</div>
				<div style={scrollListStyle}>
					{items.length === 0 && <div className="text-muted py-4 text-center">Нет агентов</div>}
					{items.map((agent, index) => (
						<div key={agent.agent_id} className="d-flex justify-content-between align-items-center gap-2 mb-3">
							<div className="d-flex gap-2 align-items-center">
								<div
									style={{
										width: 24,
										height: 24,
										borderRadius: '50%',
										background: 'var(--dark-tint-primary, #eef4ff)',
										color: '#4c8df0',
										display: 'flex',
										alignItems: 'center',
										justifyContent: 'center',
										fontSize: '12px',
										fontWeight: 700,
										flexShrink: 0,
									}}
								>
									{index + 1}
								</div>
								<div>
									<div style={{ fontWeight: 600, color: 'var(--dark-text-strong, #111827)' }}>{agent.agent_name || agent.agent_login}</div>
									<div style={mutedStyle}>работают: {agent.active_points}</div>
								</div>
							</div>
							<div className="text-end">
								<div style={{ fontWeight: 700 }}>{agent.connections_this_month}</div>
								<div style={mutedStyle}>было {agent.connections_last_month}</div>
							</div>
						</div>
					))}
				</div>
			</div>
		</div>
	)
}

function SubscriptionSummary({ subscription }) {
	const sub = subscription || {}
	const totalSubscription = Number(sub.total_subscription || 0)
	const expectedBonus = Number(sub.forecast_bonus_total || 0)
	const receivedBonus = Number(sub.bonus_received_this_month || 0)
	const receivedPercent = expectedBonus > 0 ? Math.round((receivedBonus / expectedBonus) * 100) : 0

	return (
		<div className="card mb-3" style={{ ...cardStyle, borderColor: 'var(--dark-tint-border-primary, #d4e4ff)' }}>
			<div className="card-body">
				<div className="d-flex flex-wrap justify-content-between align-items-start gap-3 mb-3">
					<div>
						<div className="d-flex align-items-center gap-2 mb-1">
							<i className="uil uil-gift" style={{ color: '#4c8df0', fontSize: '20px' }}></i>
							<span style={{ fontWeight: 700, color: 'var(--dark-text-strong, #111827)', fontSize: '16px' }}>Абонентка и бонус</span>
						</div>
						<div style={mutedStyle}>
							{sub.month_label || 'текущий месяц'}
							{sub.avg_partner_percentage ? ` · средний % ${sub.avg_partner_percentage}` : ''}
						</div>
					</div>
				</div>

				<div className="row g-3">
					<div className="col-12 col-md-4">
						<div className="p-3 h-100" style={{ background: 'var(--dark-tint-primary, #eef4ff)', borderRadius: '10px' }}>
							<div style={{ ...mutedStyle, color: '#4c8df0' }}>Абонентка ваших точек</div>
							<div style={{ fontSize: '24px', fontWeight: 700, color: '#4c8df0' }}>{formatMoney(totalSubscription)}</div>
							<div style={{ fontSize: '11px', color: 'var(--dark-text-muted, #5b6472)' }}>сумма тарифов за месяц</div>
						</div>
					</div>
					<div className="col-12 col-md-4">
						<div className="p-3 h-100" style={{ background: 'var(--dark-tint-danger, #fdecec)', borderRadius: '10px' }}>
							<div style={{ ...mutedStyle, color: 'var(--dark-fg-danger, #d23434)' }}>Ожидается бонус</div>
							<div style={{ fontSize: '24px', fontWeight: 700, color: 'var(--dark-fg-danger, #d23434)' }}>{formatMoney(expectedBonus)}</div>
							<div style={{ fontSize: '11px', color: 'var(--dark-text-muted, #5b6472)' }}>
								{sub.clients_need_topup ?? 0} точек с недостаточным балансом
							</div>
						</div>
					</div>
					<div className="col-12 col-md-4">
						<div className="p-3 h-100" style={{ background: 'var(--dark-tint-success, #e7f8ef)', borderRadius: '10px' }}>
							<div style={{ ...mutedStyle, color: 'var(--dark-fg-success, #1f9d57)' }}>Получено бонуса</div>
							<div style={{ fontSize: '24px', fontWeight: 700, color: 'var(--dark-fg-success, #1f9d57)' }}>{formatMoney(receivedBonus)}</div>
							<div style={{ fontSize: '11px', color: 'var(--dark-text-muted, #5b6472)' }}>
								{receivedPercent}% от ожидаемого
							</div>
						</div>
					</div>
				</div>

				<div style={{ fontSize: '11px', color: 'var(--dark-text-muted, #8791a1)', marginTop: '12px' }}>
					Бонус начисляется только при пополнении баланса клиентом.
				</div>
			</div>
		</div>
	)
}

function ChequePointsTable({ chequePoints }) {
	const items = chequePoints?.items || []

	const columns = [
		{
			key: 'name',
			label: 'Точка',
			render: row => (
				<>
					<div style={{ fontWeight: 600 }}>{row.name}</div>
					<div style={mutedStyle}>{row.login}</div>
				</>
			),
		},
		{
			key: 'region_name',
			label: 'Регион',
			render: row => row.region_name || '—',
		},
		{
			key: 'last_cheque_date',
			label: 'Последний чек',
			render: row => formatDateLabel(row.last_cheque_date),
		},
		{
			key: 'hours_without_cheque',
			label: 'Без чеков',
			render: row => (
				<span
					style={{
						background: 'var(--dark-tint-danger, #fdecec)',
						color: 'var(--dark-fg-danger, #d23434)',
						borderRadius: '8px',
						padding: '2px 8px',
						fontWeight: 600,
						fontSize: '12px',
					}}
				>
					{staleLabel(row.hours_without_cheque)}
				</span>
			),
		},
		{
			key: 'owner_name',
			label: 'Владелец',
			render: row => (
				<>
					<div>{row.owner_name || '—'}</div>
					<div style={mutedStyle}>{row.owner_phone || row.phone || '—'}</div>
				</>
			),
		},
		{
			key: 'agent_name',
			label: 'Агент',
			render: row => (
				<>
					<div>{row.agent_name || '—'}</div>
					<div style={mutedStyle}>{row.agent_phone || row.agent_login || '—'}</div>
				</>
			),
		},
	]

	return (
		<SectionCard
			title="Без чеков > 24 часа"
			subtitle="Клиенты, которые перестали пробивать чеки более суток назад"
		>
			<div style={scrollTableStyle}>
				<SimpleTable
					columns={columns}
					rows={items}
					emptyText="Все точки пробивают чеки"
				/>
			</div>
		</SectionCard>
	)
}

function Analytics() {
	const [loader, setLoader] = useState(true)
	const [data, setData] = useState({})

	async function getAnalytics() {
		const response = await GET('/partner/analytics')
		if (httpOk(response)) {
			setData(response.data)
			setLoader(false)
		}
	}

	useEffect(() => {
		getAnalytics()
	}, [])

	const summary = data?.summary || {}

	if (loader) {
		return (
			<>
				<Skeleton height={160} className="mb-3" />
				<div className="row mb-3">
					{[1, 2, 3, 4].map((item) => (
						<div className="col-md-3 mb-3" key={item}>
							<Skeleton height={120} />
						</div>
					))}
				</div>
				<Skeleton height={340} className="mb-3" />
				<Skeleton height={320} />
			</>
		)
	}

	return (
		<>
			<div className="mb-3">
				<h4 className="mb-1">Аналитика</h4>
				<div style={mutedStyle}>{summary.month_label}</div>
			</div>

			<SubscriptionSummary subscription={data?.subscription} />

			<div className="row g-3 mb-3">
				<div className="col-md-3">
					<SummaryKpi
						title="Работают"
						value={summary.working_clients ?? 0}
						sublabel={`чеки за 30 дней · ещё ${summary.idle_clients ?? 0} активны, но без чеков`}
						icon="uil-check-circle"
						accent="#1f9d57"
					/>
				</div>
				<div className="col-md-3">
					<SummaryKpi
						title="Не оплатили"
						value={summary.unpaid_clients ?? 0}
						sublabel={`нужна доплата: ${summary.clients_need_topup ?? 0}`}
						icon="uil-exclamation-triangle"
						accent="#d23434"
					/>
				</div>
				<div className="col-md-3">
					<SummaryKpi
						title="Подключено в этом месяце"
						value={summary.connections_this_month ?? 0}
						growth={summary.connections_growth}
						compareLabel={`к прошлому: ${summary.connections_last_month ?? 0}`}
						icon="uil-shop"
						accent="#4c8df0"
					/>
				</div>
				<div className="col-md-3">
					<SummaryKpi
						title="Агенты подключили"
						value={summary.agent_connections_this_month ?? 0}
						growth={summary.agent_connections_growth}
						compareLabel={`в прошлом: ${summary.agent_connections_last_month ?? 0}`}
						icon="uil-users-alt"
						accent="#7c3aed"
					/>
				</div>
			</div>

			<AnalyticsMonthly />

			<div className="row g-3 mb-3">
				<div className="col-lg-4">
					<AgentRanking agents={data?.agents} />
				</div>
				<div className="col-lg-8">
					<ChequePointsTable chequePoints={data?.cheque_points} />
				</div>
			</div>
		</>
	)
}

export default Analytics
